// Клиент семантики меток.
//
// Маппинг «метка → роль» — провайдер-нейтральная поверхность
// `/api/v1/settings/label-mappings` (`docs/spec/modules/repo_host/
// labels.md`): у маппинга нет провайдера, он ссылается на имена меток.
// Импорт каталога меток репозитория — удобство UI (наполнить выбор,
// подсветить неотмапленные) — отдаёт модуль трекера `task_tracker`
// (`/api/v1/connections/task-tracker/repos/{owner}/{name}/labels`), а не
// подключение forgejo. JWT прикручивает `authedFetch`.

import { authedFetch } from "../../auth/authedFetch";
import { buildJsonHeaders, HttpError } from "./api";

export { HttpError, buildJsonHeaders } from "./api";

// Одна роль каталога backend: значение и правило `role_value`.
export interface LabelRoleInfo {
  readonly role: string;
  readonly requires_value: boolean;
}

export interface LabelRolesResponse {
  readonly roles: readonly LabelRoleInfo[];
}

// Одна метка репозитория для UI-выбора и swatch'а цвета.
export interface ImportedLabel {
  readonly name: string;
  // hex без ведущего '#' (как отдаёт Forgejo, напр. '00aabb').
  readonly color: string;
  readonly description: string;
}

export interface ImportedLabelsResponse {
  readonly labels: readonly ImportedLabel[];
}

// Одно соответствие «метка → роль» в scope.
// `repository_full_name = null` — дефолт организации; не-null — override
// конкретного репозитория.
export interface LabelMappingRow {
  readonly repository_full_name: string | null;
  readonly label_name: string;
  readonly role: string;
  readonly role_value: string | null;
}

export interface LabelMappingsResponse {
  readonly mappings: readonly LabelMappingRow[];
}

export interface UpsertLabelMappingRequest {
  // null / опущено → дефолт организации.
  readonly repository_full_name?: string | null;
  readonly label_name: string;
  readonly role: string;
  // Обязателен для ролей с `requires_value` в каталоге backend; для
  // остальных ролей должен быть null/опущен.
  readonly role_value?: string | null;
}

export interface DeleteLabelMappingRequest {
  readonly repository_full_name?: string | null;
  readonly label_name: string;
  readonly role: string;
}

const MAPPINGS_URL = "/api/v1/settings/label-mappings";
const ROLES_URL = `${MAPPINGS_URL}/roles`;
const TRACKER_REPOS_URL = "/api/v1/connections/task-tracker/repos";

// `GET /repos/{owner}/{name}/labels` — метки подключённого репозитория.
// `repo` — full_name 'owner/name': ровно два сегмента, каждый
// кодируется отдельно, слэш между ними остаётся разделителем пути.
export async function getRepoLabels(
  repo: string,
): Promise<ImportedLabelsResponse> {
  const path = repo.split("/").map(encodeURIComponent).join("/");
  const response = await authedFetch(`${TRACKER_REPOS_URL}/${path}/labels`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getRepoLabels: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ImportedLabelsResponse;
}

// `GET /settings/label-mappings/roles` — каталог ролей в порядке backend.
export async function getLabelRoles(): Promise<LabelRolesResponse> {
  const response = await authedFetch(ROLES_URL, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getLabelRoles: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as LabelRolesResponse;
}

// `GET /settings/label-mappings?repo=` — строки одного scope. Пустой/
// опущенный `repo` → дефолты организации.
export async function getLabelMappings(
  repo?: string | null,
): Promise<LabelMappingsResponse> {
  const query =
    repo !== undefined && repo !== null && repo !== ""
      ? `?repo=${encodeURIComponent(repo)}`
      : "";
  const response = await authedFetch(`${MAPPINGS_URL}${query}`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `getLabelMappings: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as LabelMappingsResponse;
}

// `POST /settings/label-mappings` — создать/обновить соответствие.
// Право `repo.labels.manage` (admin), 403 без него.
export async function createLabelMapping(
  body: UpsertLabelMappingRequest,
): Promise<LabelMappingRow> {
  const response = await authedFetch(MAPPINGS_URL, {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `createLabelMapping: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as LabelMappingRow;
}

// `DELETE /settings/label-mappings` — удалить соответствие (идемпотентно,
// 204). Право `repo.labels.manage` (admin), 403 без него.
export async function deleteLabelMapping(
  body: DeleteLabelMappingRequest,
): Promise<void> {
  const response = await authedFetch(MAPPINGS_URL, {
    method: "DELETE",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `deleteLabelMapping: HTTP ${String(response.status)}`,
    );
  }
}
